import React from "react";
import { motion } from "framer-motion";
import { Badge } from "@/shared/components/ui/badge";
import { Button } from "@/shared/components/ui/button";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import {
  ArrowDownToLine,
  ArrowUpFromLine,
  MoreHorizontal,
  Eye,
  RefreshCw,
  XCircle,
  Copy,
  CheckCircle2,
  Clock,
  Loader2,
  AlertTriangle,
} from "lucide-react";
import { format } from "date-fns";
import { Skeleton } from "@/shared/components/ui/skeleton";

const getStatusConfig = (t) => {
  const defaultConfig = {
    success: { icon: CheckCircle2, color: "bg-emerald-100 text-emerald-700 border-emerald-200", label: "Success" },
    pending: { icon: Clock, color: "bg-amber-100 text-amber-700 border-amber-200", label: "Pending" },
    processing: { icon: Loader2, color: "bg-blue-100 text-blue-700 border-blue-200", label: "Processing" },
    failed: { icon: XCircle, color: "bg-red-100 text-red-700 border-red-200", label: "Failed" },
    expired: { icon: AlertTriangle, color: "bg-slate-100 text-slate-700 border-slate-200", label: "Expired" },
    cancelled: { icon: XCircle, color: "bg-slate-100 text-slate-700 border-slate-200", label: "Cancelled" },
    declined: { icon: XCircle, color: "bg-red-100 text-red-700 border-red-200", label: "Declined" },
  };
  
  if (!t) return defaultConfig;
  
  return {
    success: { ...defaultConfig.success, label: t("success") },
    pending: { ...defaultConfig.pending, label: t("pending") },
    processing: { ...defaultConfig.processing, label: t("processing") },
    failed: { ...defaultConfig.failed, label: t("failed") },
    expired: { ...defaultConfig.expired, label: t("expired") },
    cancelled: { ...defaultConfig.cancelled, label: t("cancelled") },
    declined: { ...defaultConfig.declined, label: t("declined") || "Declined" },
  };
};

const paymentMethodLabels = {
  promptpay_qr: "PromptPay QR",
  bank_transfer: "Bank Transfer",
  truemoney: "TrueMoney",
  linepay: "LinePay",
  shopeepay: "ShopeePay",
  card: "Card",
};

export default function TransactionTable({
  transactions = [],
  isLoading,
  onView,
  onRetry,
  onCancel,
  merchants = [],
  resellers = [],
  isQRCode = false,
}) {
  let t = null;
  try {
    const langContext = useLanguage();
    t = langContext?.t;
  } catch (e) {
    // Not wrapped in LanguageProvider
  }
  
  // Fallback function if t is not available
  const translate = (key) => {
    if (t) return t(key);
    const fallbacks = {
      transaction: "Transaction",
      reseller: "Reseller", 
      merchant: "Merchant",
      provider: "Provider",
      method: "Method",
      amount: "Amount",
      status: "Status",
      date: "Date",
      transactions: "No transactions",
      ref: "Ref",
      direct: "Direct",
      viewDetails: "View Details",
      retry: "Retry",
      cancel: "Cancel",
    };
    return fallbacks[key] || key;
  };
  
  // Helper to get merchant name - prefer stored name, fallback to lookup
  const getMerchantName = (tx) => {
    if (isQRCode && tx.merchant) {
      return tx.merchant.name || tx.merchant.code || "N/A";
    }
    if (tx.merchant_name) return tx.merchant_name;
    const merchant = merchants.find(m => m.id === tx.merchant_id || m.code === tx.merchant_code);
    return merchant?.name || tx.merchant_code || "N/A";
  };

  // Helper to get reseller name - prefer stored name, fallback to lookup
  const getResellerName = (tx) => {
    if (isQRCode) {
      // For QR codes, check merchant.createdByResellerId
      if (tx.merchant?.createdByResellerId && tx.reseller) {
        return tx.reseller.name;
      }
      return null;
    }
    if (tx.reseller_name) return tx.reseller_name;
    const reseller = resellers.find(r => r.id === tx.reseller_id || r.code === tx.reseller_code);
    return reseller?.name || null;
  };
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
        <div className="p-4 space-y-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="flex items-center gap-4">
              <Skeleton className="h-10 w-10 rounded-lg" />
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-6 w-20 rounded-full" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="font-semibold">{translate("transaction")}</TableHead>
              <TableHead className="font-semibold">{translate("reseller")}</TableHead>
              <TableHead className="font-semibold">{translate("merchant")}</TableHead>
              <TableHead className="font-semibold">{translate("provider")}</TableHead>
              <TableHead className="font-semibold">{translate("method")}</TableHead>
              <TableHead className="font-semibold text-right">{translate("amount")}</TableHead>
              <TableHead className="font-semibold">{translate("status")}</TableHead>
              <TableHead className="font-semibold">{translate("date")}</TableHead>
              <TableHead className="font-semibold w-12"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {transactions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-12 text-slate-500">
                  {translate("transactions")}
                </TableCell>
              </TableRow>
            ) : (
              transactions.map((tx, index) => {
                const statusConfig = getStatusConfig(t);
                const statusKey = (tx.status || '').toLowerCase();
                const status = statusConfig[statusKey] || statusConfig.pending;
                const StatusIcon = status.icon;

                return (
                  <motion.tr
                    key={tx.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.02 * index }}
                    className="hover:bg-slate-50 transition-colors group"
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-blue-100">
                          <ArrowDownToLine className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-slate-900 font-mono text-sm">
                              {isQRCode ? (tx.payment_id || "") : (tx.transaction_id || "")}
                            </span>
                            <button
                              onClick={() => copyToClipboard(isQRCode ? tx.payment_id : tx.transaction_id)}
                              className="opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <Copy className="h-3 w-3 text-slate-400 hover:text-slate-600" />
                            </button>
                          </div>
                          {isQRCode && tx.reference && (
                            <span className="text-xs text-slate-500">
                              {translate("ref")}: {tx.reference}
                            </span>
                          )}
                          {!isQRCode && tx.merchant_ref && (
                            <span className="text-xs text-slate-500">
                              {translate("ref")}: {tx.merchant_ref}
                            </span>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const resellerName = getResellerName(tx);
                        return resellerName ? (
                          <span className="font-medium text-sm">{resellerName}</span>
                        ) : (
                          <span className="text-xs text-slate-400">{translate("direct")}</span>
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      <span className="font-medium">{getMerchantName(tx)}</span>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="font-normal">
                        {isQRCode ? (tx.client_bank_name || "N/A") : (tx.provider_code || "N/A")}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-slate-600">
                        {isQRCode ? (t ? t("qrCode") : "QR Code") : (paymentMethodLabels[tx.payment_method] || tx.payment_method)}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <span className="font-semibold text-blue-600">
                        +฿{isQRCode ? (tx.amount?.toLocaleString()) : (tx.amount?.toLocaleString())}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge className={`${status.color} gap-1 border`}>
                        <StatusIcon
                          className={`h-3 w-3 ${
                            statusKey === "processing" ? "animate-spin" : ""
                          }`}
                        />
                        {status.label}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <div className="text-slate-900">
                          {isQRCode 
                            ? (tx.createdAt ? format(new Date(tx.createdAt), "MMM d, yyyy") : "N/A")
                            : (tx.created_date ? format(new Date(tx.created_date), "MMM d, yyyy") : "N/A")
                          }
                        </div>
                        <div className="text-slate-500">
                          {isQRCode
                            ? (tx.createdAt ? format(new Date(tx.createdAt), "HH:mm:ss") : "")
                            : (tx.created_date ? format(new Date(tx.created_date), "HH:mm:ss") : "")
                          }
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => onView?.(tx)}>
                            <Eye className="h-4 w-4 mr-2" />
                            {translate("viewDetails")}
                          </DropdownMenuItem>
                          {(tx.status === "failed" || tx.status === "expired") && (
                            <DropdownMenuItem onClick={() => onRetry?.(tx)}>
                              <RefreshCw className="h-4 w-4 mr-2" />
                              {translate("retry")}
                            </DropdownMenuItem>
                          )}
                          {tx.status === "pending" && (
                            <DropdownMenuItem
                              onClick={() => onCancel?.(tx)}
                              className="text-red-600"
                            >
                              <XCircle className="h-4 w-4 mr-2" />
                              {translate("cancel")}
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </motion.tr>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}